import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@nextui-org/react";
import axios from "axios";
import { DeleteScrapbookFilesContext } from "Context/DeleteScrapbookFilesContext";
import { PopUpContext } from "Context/PopUpContext";
import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import checkIcon from "../../../Assets/my-fronds/check.svg";
import useSettings from "hooks/useSettings";
import { SettingsContext } from "Context/SettingsContext";
import { API_BASE_URL } from "../../../config/apiConfig";

// DOCX to HTML Viewer Component using mammoth.js
const DocxHtmlViewer = ({ fileUrl, fileName, onClose }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fileSize, setFileSize] = useState(0);

  useEffect(() => {
    loadDocxFile();
  }, [fileUrl]);

  const loadDocxFile = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Fetch the DOCX file
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      
      // Get file size
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const sizeMB = parseInt(contentLength) / (1024 * 1024);
        setFileSize(sizeMB);
        
        // Warn about large files
        if (sizeMB > 5) {
          toast.info("Large file detected - loading may take a moment", {
            autoClose: 3000,
          });
        }
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      // Dynamically import mammoth only when needed
      const mammoth = await import('mammoth');
      
      // Convert DOCX to HTML
      const result = await mammoth.default.convertToHtml({ arrayBuffer: arrayBuffer });
      
      // Set the HTML content with enhanced styling
      const styledHtml = `
        <div class="docx-content">
          ${result.value}
        </div>
      `;
      
      setHtmlContent(styledHtml);
      
      // Show any conversion warnings
      if (result.messages && result.messages.length > 0) {
        console.log('Conversion messages:', result.messages);
      }
      
    } catch (err) {
      console.error('Error loading DOCX:', err);
      setError(`Failed to load document: ${err.message}`);
      
      // Fallback to plain text extraction
      try {
        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const textDecoder = new TextDecoder('utf-8');
        const text = textDecoder.decode(arrayBuffer);
        // Clean up binary data for display
        const cleanText = text.replace(/[^\x20-\x7E\n\r\t]/g, ' ').substring(0, 5000);
        setHtmlContent(`<div class="plain-text-fallback"><pre>${cleanText}</pre></div>`);
        setError("Showing extracted text (formatting may be lost)");
      } catch (fallbackErr) {
        setError("Cannot display this document. Please download it.");
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printDocument = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${fileName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            .docx-content h1 { font-size: 24px; margin: 20px 0; }
            .docx-content h2 { font-size: 20px; margin: 18px 0; }
            .docx-content p { margin: 12px 0; line-height: 1.6; }
            .docx-content ul, .docx-content ol { margin-left: 30px; }
            .docx-content table { border-collapse: collapse; margin: 15px 0; }
            .docx-content th, .docx-content td { 
              border: 1px solid #ddd; padding: 8px; 
              text-align: left; 
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <h1>${fileName}</h1>
          ${htmlContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Inline styles for the viewer
  const viewerStyles = `
    .docx-content {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 210mm; /* A4 width */
      margin: 0 auto;
    }
    .docx-content h1 {
      font-size: 28px;
      font-weight: bold;
      margin: 24px 0 16px;
      color: #1a1a1a;
      border-bottom: 2px solid #eaeaea;
      padding-bottom: 8px;
    }
    .docx-content h2 {
      font-size: 24px;
      font-weight: 600;
      margin: 20px 0 14px;
      color: #2c2c2c;
    }
    .docx-content h3 {
      font-size: 20px;
      font-weight: 600;
      margin: 18px 0 12px;
      color: #3c3c3c;
    }
    .docx-content p {
      margin: 12px 0;
      font-size: 16px;
    }
    .docx-content ul, .docx-content ol {
      margin-left: 32px;
      margin: 12px 0;
    }
    .docx-content li {
      margin: 6px 0;
    }
    .docx-content table {
      border-collapse: collapse;
      margin: 16px 0;
      width: 100%;
      border: 1px solid #ddd;
      font-size: 14px;
    }
    .docx-content th {
      background-color: #f5f5f5;
      font-weight: bold;
      padding: 10px;
      border: 1px solid #ddd;
      text-align: left;
    }
    .docx-content td {
      padding: 10px;
      border: 1px solid #ddd;
    }
    .docx-content strong, .docx-content b {
      font-weight: bold;
    }
    .docx-content em, .docx-content i {
      font-style: italic;
    }
    .docx-content u {
      text-decoration: underline;
    }
    .docx-content blockquote {
      border-left: 4px solid #ddd;
      margin: 16px 0;
      padding-left: 16px;
      color: #666;
      font-style: italic;
    }
    .plain-text-fallback pre {
      white-space: pre-wrap;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      background: #f9f9f9;
      padding: 20px;
      border-radius: 8px;
      border: 1px solid #eee;
    }
  `;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{viewerStyles}</style>
      
      <div className="bg-white rounded-xl w-full max-w-6xl h-5/6 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg">DOC</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">{fileName}</h2>
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <span className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  HTML Preview
                </span>
                {fileSize > 0 && (
                  <span>• {fileSize.toFixed(1)} MB</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={printDocument}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all hover:shadow-sm flex items-center space-x-2"
              title="Print document"
            >
              <span className="text-lg">🖨️</span>
              <span className="hidden sm:inline">Print</span>
            </button>
            <button
              onClick={downloadFile}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:opacity-90 transition-all hover:shadow-lg flex items-center space-x-2"
            >
              <span className="text-lg">⬇️</span>
              <span>Download</span>
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-2xl text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Toolbar */}
          <div className="px-5 py-3 border-b bg-gray-50 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {error && (
                <span className="flex items-center text-amber-600">
                  <span className="mr-2">⚠️</span>
                  {error}
                </span>
              )}
              {!error && (
                <span>DOCX rendered as HTML • Powered by mammoth.js</span>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                ↑ Top
              </button>
              <button
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                ↓ Bottom
              </button>
            </div>
          </div>
          
        </div>
        
    
      </div>
    </div>
  );
};

// Main ScrapbookFiles Component
export default function ScrapbookFiles() {
  const { frondId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [frondData, setFrondData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  let authToken = localStorage.getItem("token");

  const getFilesFromResponse = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.files)) return payload.files;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.files)) return payload.data.files;
    if (Array.isArray(payload?.response?.data)) return payload.response.data;
    if (Array.isArray(payload?.response?.data?.files)) return payload.response.data.files;
    return [];
  };

  const getScrapbookFiles = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/scrapbook-files/${frondId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setFrondData(getFilesFromResponse(data));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load files", {
        autoClose: 1500,
        position: "bottom-right",
        hideProgressBar: true,
        className: "rounded-4xl custom-toast error-toast",
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getScrapbookFiles();
  }, []);

  const { isAddScrapbookFile } = useContext(PopUpContext);

  useEffect(() => {
    if (isAddScrapbookFile) {
      getScrapbookFiles();
    }
  }, [isAddScrapbookFile]);

  const {
    openDeleteFilesCheckbox,
    deleteFilesProjects,
    setDeleteFilesProjects,
  } = useContext(DeleteScrapbookFilesContext);

  const toggleDeleteFile = (fileId) => {
    if (deleteFilesProjects.includes(fileId)) {
      setDeleteFilesProjects(deleteFilesProjects.filter((id) => id !== fileId));
    } else {
      setDeleteFilesProjects([...deleteFilesProjects, fileId]);
    }
  };

  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    const timeUnits = [
      { name: "year", seconds: 31536000 },
      { name: "month", seconds: 2592000 },
      { name: "day", seconds: 86400 },
      { name: "hour", seconds: 3600 },
      { name: "minute", seconds: 60 },
      { name: "second", seconds: 1 },
    ];

    for (const unit of timeUnits) {
      const interval = Math.floor(diffInSeconds / unit.seconds);
      if (interval >= 1) {
        return `${interval} ${unit.name}${interval > 1 ? "s" : ""} ago`;
      }
    }

    return "just now";
  };

  const handleDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || url.split("/").pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileName = (filePath) => {
    if (!filePath) return "";
    const parts = filePath.split("/");
    const fullName = parts[parts.length - 1];
    return fullName.substring(0, fullName.lastIndexOf("."));
  };

  const getFileExtension = (filePath) => {
    if (!filePath) return "";
    const parts = filePath.split(".");
    return parts[parts.length - 1].toUpperCase();
  };

  const getFileType = (filePath) => {
    if (!filePath) return "";
    const ext = getFileExtension(filePath).toLowerCase();
    
    if (['doc', 'docx'].includes(ext)) {
      return "Document";
    } else if (['pdf'].includes(ext)) {
      return "PDF";
    } else if (['xls', 'xlsx'].includes(ext)) {
      return "Spreadsheet";
    } else if (['ppt', 'pptx'].includes(ext)) {
      return "Presentation";
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return "Image";
    } else if (['txt', 'rtf', 'md'].includes(ext)) {
      return "Text";
    } else if (['zip', 'rar', '7z'].includes(ext)) {
      return "Archive";
    } else {
      return "File";
    }
  };

  const getFileIcon = (filePath) => {
    const ext = getFileExtension(filePath).toLowerCase();
    
    if (['doc', 'docx'].includes(ext)) {
      return "📄";
    } else if (['pdf'].includes(ext)) {
      return "📕";
    } else if (['xls', 'xlsx'].includes(ext)) {
      return "📊";
    } else if (['ppt', 'pptx'].includes(ext)) {
      return "📽️";
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)) {
      return "🖼️";
    } else if (['zip', 'rar', '7z'].includes(ext)) {
      return "📦";
    } else if (['txt', 'rtf', 'md'].includes(ext)) {
      return "📝";
    } else {
      return "📁";
    }
  };

  const isDocxFile = (filePath) => {
    if (!filePath) return false;
    const ext = getFileExtension(filePath).toLowerCase();
    return ['doc', 'docx'].includes(ext);
  };

  const isViewableInBrowser = (filePath) => {
    if (!filePath) return false;
    const ext = getFileExtension(filePath).toLowerCase();
    return [
      'pdf',
      'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp',
      'txt', 'html', 'htm'
    ].includes(ext);
  };

  const handleFileAction = (fileUrl, fileName, fileType) => {
    if (!fileUrl) return;
    
    const ext = getFileExtension(fileUrl).toLowerCase();
    
    if (isDocxFile(fileUrl)) {
      // Open DOCX in HTML viewer
      setSelectedFile({ url: fileUrl, name: fileName, type: 'docx' });
    } else if (isViewableInBrowser(fileUrl)) {
      // Open directly in browser
      window.open(fileUrl, '_blank');
    } else {
      // Download other file types
      handleDownload(fileUrl, fileName);
      toast.info(`${getFileType(fileUrl)} downloaded`, {
        autoClose: 1500,
      });
    }
  };

  const columns = [
    { key: "name", label: "File Name" },
    { key: "type", label: "Type" },
    { key: "date", label: "Uploaded" },
    { key: "actions", label: "Actions" },
  ];

  useSettings({ projectId: frondId });
  const { frondSettings } = useContext(SettingsContext);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Pagination
  const ITEMS_PER_PAGE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (currentPage > Math.ceil(frondData.length / ITEMS_PER_PAGE)) {
      setCurrentPage(Math.max(Math.ceil(frondData.length / ITEMS_PER_PAGE), 1));
    }
  }, [frondData, currentPage]);

  const totalPages = Math.ceil(frondData.length / ITEMS_PER_PAGE);
  const currentItems = frondData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleGoToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <section className="scrapbook-files">
      {/* DOCX HTML Viewer Modal */}
      {selectedFile && selectedFile.type === 'docx' && (
        <DocxHtmlViewer
          fileUrl={selectedFile.url}
          fileName={selectedFile.name}
          onClose={() => setSelectedFile(null)}
        />
      )}
      
      {/* Loading State */}
      {isLoading && frondData.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading files...</p>
        </div>
      )}
      
      {/* Files Table */}
      {!isLoading && frondData?.length > 0 ? (
        <>
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Scrapbook Files</h2>
                <p className="text-gray-600 text-sm mt-1">
                  {frondData.length} files 
                </p>
              </div>
              <div className="text-sm text-gray-500">
                
              </div>
            </div>
          </div>
          
          <Table
            classNames={{
              wrapper: "rounded-xl border border-gray-200 shadow-sm",
              th: "text-white font-bold text-sm bg-gradient-to-r from-blue-600 to-blue-900 py-4",
              td: "p-4 bg-white group-hover:bg-blue-50 transition-colors",
              tr: "group hover:bg-blue-50 transition-colors",
            }}
            aria-label="Scrapbook Files"
          >
            <TableHeader>
              {columns.map((column) => (
                <TableColumn key={column.key}>{column.label}</TableColumn>
              ))}
            </TableHeader>
            <TableBody>
              {currentItems.map((item) => {
                const fileName = getFileName(item?.image);
                const fileExtension = getFileExtension(item?.image);
                const fileType = getFileType(item?.image);
                const fileUrl = item?.image;
                const isDocx = isDocxFile(fileUrl);
                const isViewable = isViewableInBrowser(fileUrl);
                
                return (
                  <TableRow key={item?.id}>
                    <TableCell className="flex items-center">
                      <button
                        type="button"
                        onClick={() => toggleDeleteFile(item.id)}
                        className={`rounded-[7px] self-start bg-transparent transition-all duration-300 overflow-hidden shrink-0 ${
                          openDeleteFilesCheckbox
                            ? "w-[29px] h-[29px] mr-4"
                            : "w-0 h-0 mr-0"
                        }`}
                        aria-label="Select file for deletion"
                      >
                        <div className="w-[21px] h-[21px] border-gray-300 rounded-[7px] border-2 border-solid relative flex justify-center items-center">
                          <div
                            className={`${
                              deleteFilesProjects.includes(item.id)
                                ? "opacity-100"
                                : "opacity-0"
                            } flex justify-center absolute inset-[-2px] items-center rounded-[7px] bg-blue-100 transition-opacity duration-300`}
                          >
                            <img
                              src={checkIcon}
                              alt="Check"
                              className="w-[12.61px] h-[8.41px]"
                            />
                          </div>
                        </div>
                      </button>
                      
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${
                          isDocx 
                            ? 'bg-gradient-to-br from-blue-100 to-blue-200 text-blue-600'
                            : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600'
                        }`}>
                          {getFileIcon(fileUrl)}
                        </div>
                        <div className="min-w-0">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleFileAction(fileUrl, fileName, fileType);
                            }}
                            className="text-left hover:text-blue-600 transition-colors"
                          >
                            <div className="font-semibold text-gray-800 truncate max-w-xs">
                              {fileName || "Unnamed File"}
                            </div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {fileUrl ? new URL(fileUrl).hostname : ""}
                            </div>
                          </button>
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-700">{fileType}</span>
                        <span className="text-xs text-gray-500">{fileExtension}</span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-gray-700">{timeAgo(item?.created_at) || "-"}</span>
                        <span className="text-xs text-gray-500">
                          {item?.created_at ? new Date(item.created_at).toLocaleDateString() : "-"}
                        </span>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {isDocx && (
                          <button
                            // onClick={(e) => {
                            //   e.preventDefault();
                            //   setSelectedFile({ url: fileUrl, name: fileName, type: 'docx' });
                            // }}
                            onClick={(e) => window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}`, '_blank')}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium flex items-center space-x-2 shadow-sm"
                            title="Preview DOCX as HTML"
                          >
                            {/* <span>👁️</span> */}
                            <span>view</span>
                          </button>
                        )}
                        
                        {!isDocx && isViewable && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(fileUrl, '_blank');
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium flex items-center space-x-2 shadow-sm"
                          >
                            {/* <span>👁️</span> */}
                            <span>View</span>
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownload(fileUrl, fileName + '.' + fileExtension.toLowerCase());
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex items-center space-x-2 shadow-sm"
                        >
                          {/* <span>⬇️</span> */}
                          <span>Download</span>
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      ) : !isLoading && (
        <div className="text-center py-16">
          <div className="text-7xl mb-6 opacity-20">📁</div>
          <h3 className="text-2xl font-semibold text-gray-400 mb-3">No files yet</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Upload DOCX, PDF, images, or other files to your scrapbook. 
            DOCX files can be previewed directly in your browser.
          </p>
        </div>
      )}
      
      {/* Pagination */}
      {frondData?.length > ITEMS_PER_PAGE && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8 p-4 bg-gray-50 rounded-xl">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
            {Math.min(currentPage * ITEMS_PER_PAGE, frondData.length)} of{" "}
            {frondData.length} files
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <span>←</span>
              <span>Previous</span>
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handleGoToPage(pageNum)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                      currentPage === pageNum
                        ? "bg-blue-600 text-white"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    } transition-colors`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              <span>Next</span>
              <span>→</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
